// HAVI USAGE-LEKÉRÉS INDÍTÓ GOMB — a látott hónapra kérdezi meg a külső rendszereket.
//
// MIÉRT VAN a cron mellett: a hó eleji automata futás után gyakori, hogy a külső riport később áll össze
// (vagy javul) — ilyenkor a kolléga innen újrakérheti a hónapot. Az írás a természetes kulcson felülír,
// tehát az újrakérés SOHA nem duplikál.
//
// A VISSZAJELZÉS BESZÉDES: a toast megmondja, hány sor frissült, és MIÉRT hiányzik, ami hiányzik
// (`skipped`: nincs adatforrás / nincs beállítva / a külső rendszer nem válaszolt). A gépi okot a felület
// fordítja — a szerver nem küld emberi szöveget.
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type TFunction } from 'i18next';
import { toast } from 'sonner';

import { usageKeys } from '../api';

import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

function useUsageFetchInternal({
  month,
  monthLabel,
  t,
}: {
  month: string;
  monthLabel: string;
  t: TFunction;
}): { run: () => void; pending: boolean } {
  const qc = useQueryClient();
  const [yearStr, monthStr] = month.split('-');

  const fetchMutation = useMutation({
    mutationFn: () =>
      api.serviceUsageFetch.mutation({
        body: { year: Number(yearStr), month: Number(monthStr) },
      }),
    onSuccess: (res) => {
      if (res.status === 200) {
        const body = res.body;
        // A BUKOTT ÍRÁSOK KÜLÖN, HANGOSAN. A `written` szám önmagában elrejtené: egy fojtott köteg így
        // nézett ki, hogy „12 sor mentve", miközben sorok százai nem íródtak ki.
        if (body.failed > 0) {
          toast.error(t('usageData.fetch.failedRows', { count: body.failed }));
        }
        if (body.written > 0) {
          toast.success(
            t('usageData.fetch.done', { month: monthLabel, written: body.written }),
            body.skipped.length > 0
              ? {
                  description: t('usageData.fetch.skipped', {
                    list: body.skipped
                      .map(
                        (s) =>
                          `${t(`usageData.sourceType.${s.sourceType}`, { defaultValue: s.sourceType })} (${t(
                            `usageData.fetch.reason.${s.reason}`,
                            { defaultValue: s.reason },
                          )})`,
                      )
                      .join(', '),
                  }),
                }
              : undefined,
          );
        } else {
          // Nulla sor: nem hiba, de nem is siker — a hiány OKÁVAL együtt mondjuk el.
          toast.info(t('usageData.fetch.empty', { month: monthLabel }), {
            description:
              body.skipped.length > 0
                ? t('usageData.fetch.skipped', {
                    list: body.skipped
                      .map(
                        (s) =>
                          `${t(`usageData.sourceType.${s.sourceType}`, { defaultValue: s.sourceType })} (${t(
                            `usageData.fetch.reason.${s.reason}`,
                            { defaultValue: s.reason },
                          )})`,
                      )
                      .join(', '),
                  })
                : undefined,
          });
        }
        void qc.invalidateQueries({ queryKey: usageKeys.all });
      } else if (res.status === 403) {
        toast.error(t('toast:usageData.fetchForbidden'));
      } else {
        toast.error(t('toast:usageData.fetchError'));
      }
    },
    onError: () => toast.error(t('toast:usageData.fetchError')),
  });

  return { run: () => fetchMutation.mutate(), pending: fetchMutation.isPending };
}

/**
 * A HAVI LEKÉRÉS VEZÉRLŐJE — a mutáció EGY példányban, mert KÉT felület indítja: a deszktop sáv-gombja és a
 * MOBIL lebegő FAB (ahol a sáv-gombok rejtve vannak). Külön példányokkal a „fut éppen" állapot szétcsúszna.
 */
export function useUsageFetch({
  month,
  monthLabel,
  t,
}: {
  month: string;
  monthLabel: string;
  t: TFunction;
}): { run: () => void; pending: boolean } {
  return useUsageFetchInternal({ month, monthLabel, t });
}

export function UsageFetchButton({
  month,
  monthLabel,
  canManage,
  t,
}: {
  /** A látott hónap `YYYY-MM` alakban (a lekérés cél-hónapja). */
  month: string;
  /** A hónap emberi felirata a visszajelzéshez. */
  monthLabel: string;
  /**
   * `usage_data.manage` — a LEKÉRÉS a lap írás-akciója (külső rendszereket kérdez és sorokat ír), ezért a
   * megtekintés-jog nem elég hozzá. Jog nélkül a gomb MEG SEM JELENIK: a szerver 403-mal zárná, egy
   * felkínált, garantáltan bukó gomb pedig hamis affordancia.
   */
  canManage: boolean;
  t: TFunction;
}): JSX.Element | null {
  const fetchMutation = useUsageFetch({ month, monthLabel, t });

  // A hook-ok FÖLÖTTE futnak le (a hook-sorrend nem lehet feltételes) — a jog-kapu csak a renderelést zárja.
  if (!canManage) return null;

  return (
    <Button
      variant="outline"
      className="shrink-0"
      title={t('usageData.fetch.title')}
      disabled={fetchMutation.pending}
      onClick={fetchMutation.run}
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
      <span className="hidden sm:inline">
        {fetchMutation.pending ? t('usageData.fetch.running') : t('usageData.fetch.action')}
      </span>
    </Button>
  );
}
